import { Bell, Check, CheckCheck, ShoppingCart, Users, Info } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useRef, useEffect } from 'react'
import { useNotificationsState } from '../../state/notifications'
import { useTranslation } from '../../hooks/useTranslation'
import { cn } from '@/lib/utils'
import type { Notification, NotificationType } from '../../models/notification'

const NOTIFICATION_ICONS: Record<NotificationType, typeof Bell> = {
  purchase: ShoppingCart,
  referral: Users,
  system: Info
}

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  purchase: 'bg-blue-500/10 text-blue-500',
  referral: 'bg-green-500/10 text-green-500',
  system: 'bg-purple-500/10 text-purple-500'
}

function formatRelativeTime(dateString: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return t('common.time.justNow')
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return t('common.time.minutesAgo', { count: minutes })
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return t('common.time.hoursAgo', { count: hours })
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400)
    return t('common.time.daysAgo', { count: days })
  } else {
    const weeks = Math.floor(diffInSeconds / 604800)
    return t('common.time.weeksAgo', { count: weeks })
  }
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const {
    notifications,
    unreadCount,
    dataIsLoading,
    markAsRead,
    markAllAsRead,
    markAsReadLoading,
    markAllAsReadLoading
  } = useNotificationsState()
  const { t } = useTranslation()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
  }

  const handleMarkAllAsRead = () => {
    markAllAsRead(undefined)
  }

  const displayCount = unreadCount > 99 ? '99+' : unreadCount

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative flex items-center justify-center h-10 w-10 rounded-xl transition-colors',
          'bg-muted hover:bg-muted/80 text-muted-foreground'
        )}
        style={{ minHeight: '44px', minWidth: '44px' }}
      >
        <Bell size={20} />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className={cn(
                'absolute -top-1 -right-1 flex items-center justify-center',
                'min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold',
                'bg-destructive text-destructive-foreground'
              )}
            >
              {displayCount}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="notification-dropdown"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'absolute right-0 mt-2 w-80 sm:w-96 z-50',
              'bg-popover border border-border rounded-xl shadow-lg',
              'max-h-[80vh] flex flex-col'
            )}
          >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h3 className="font-semibold text-foreground">
                  {t('notifications.title')}
                </h3>
                {unreadCount > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleMarkAllAsRead}
                    disabled={markAllAsReadLoading}
                    className={cn(
                      'flex items-center gap-1 text-xs text-primary hover:text-primary/80',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    <CheckCheck size={14} />
                    {t('notifications.markAllAsRead')}
                  </motion.button>
                )}
              </div>

              {/* Notifications List */}
              <div className="flex-1 overflow-y-auto max-h-[60vh]">
                {dataIsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Bell className="h-12 w-12 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {t('notifications.noNotifications')}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {notifications.map((notification) => {
                      const Icon = NOTIFICATION_ICONS[notification.type]
                      const colorClass = NOTIFICATION_COLORS[notification.type]

                      return (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
                          onClick={() => handleNotificationClick(notification)}
                          className={cn(
                            'flex items-start gap-3 p-4 cursor-pointer transition-colors',
                            !notification.is_read && 'bg-primary/5'
                          )}
                        >
                          <div className={cn(
                            'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
                            colorClass
                          )}
                          >
                            <Icon size={18} />
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              'text-sm font-medium truncate',
                              !notification.is_read && 'text-foreground'
                            )}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                              {notification.message}
                            </p>
                            <p className="text-xs text-muted-foreground/70 mt-1">
                              {formatRelativeTime(notification.created_at, t)}
                            </p>
                          </div>

                          {!notification.is_read && (
                            <div className="flex-shrink-0">
                              {markAsReadLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-primary" />
                              )}
                            </div>
                          )}

                          {notification.is_read && (
                            <div className="flex-shrink-0 text-muted-foreground/50">
                              <Check size={14} />
                            </div>
                          )}
                        </motion.div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-border">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsOpen(false)}
                  className="w-full text-center text-sm text-primary hover:text-primary/80 font-medium"
                >
                  {t('notifications.viewAll')}
                </motion.button>
              </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
